import { useEffect, useState } from 'react';
import { omit } from 'lodash-es';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { showError, getCleanedUrlParams } from '@utils';
import { boardsServices } from '@services';
import { BOARDS, BOARDS_ID } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useTeams } from 'components/common/QuickTicketCreationForm/hooks';
import { getGenericTicketsPayload, handleGenericTickets } from './utils';

export const GENERIC_TICKETS_PAGE_SIZE = 50;

export const useGenericTicketsOperations = () => {
  const router = useRouter();
  const [filters, setFilters] = useState({});
  const {
    activeTeams: teams,
    isFetched: teamsFetched,
    isLoading: teamsLoading,
  } = useTeams();
  const { query, isReady } = router;
  const {
    page = 1,
    pageSize = GENERIC_TICKETS_PAGE_SIZE,
    orderBy = 'createdAt',
    order = 'desc',
    query: searchQuery,
    dateFrom,
    dateTo,
    status,
    assignee,
    team,
    dueDate,
    priority,
    reason,
    createdBy,
  } = query;

  const params = getCleanedUrlParams({
    page,
    pageSize: parseInt(pageSize, 10) || GENERIC_TICKETS_PAGE_SIZE,
    orderBy,
    order,
    query: searchQuery,
    dateFrom,
    dateTo,
    status,
    assignee,
    dueDate,
    team,
    priority,
    reason,
    createdBy,
  });

  const queryParams = {
    ...params,
    boardId: BOARDS_ID.GENERIC_TICKETS,
    boardType: BOARDS.GENERIC_TICKETS,
  };

  const payload = getGenericTicketsPayload(queryParams);

  const {
    data: genericTicketsData,
    isLoading: isGenericTicketsLoading,
    isError: isGenericTicketsError,
    error: genericTicketsError,
    refetch: refetchGenericTickets,
  } = useQuery(
    [QUERY_KEYS.GET_GENERIC_TICKETS_KEY, params, filters],
    () => {
      return boardsServices.getBoardTasks({
        boardId: BOARDS_ID.GENERIC_TICKETS,
        params: {
          ...omit(params, [
            'dateFrom',
            'dateTo',
            'status',
            'dueDate',
            'team',
            'assignee',
            'priority',
            'reason',
            'createdBy',
            'boardId',
            'boardType',
          ]),
        },
        payload,
      });
    },
    {
      enabled: isReady && teamsFetched,
      refetchOnMount: true,
    }
  );
  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, BOARDS_ID.GENERIC_TICKETS],
    () => boardsServices.getEnumsPerBoardId(BOARDS_ID.GENERIC_TICKETS),
    { enabled: router.isReady }
  );

  const tickets = handleGenericTickets({
    tickets: genericTicketsData?.data || [],
    teams,
  });

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleChangeDateRange = (range) => {
    const [from, to] = range;
    router.push({
      query: {
        ...router.query,
        dateFrom: from ? from.toISOString() : undefined,
        dateTo: to ? to.toISOString() : undefined,
      },
    });
  };

  const handleClearDateRange = () => {
    delete router.query.dateFrom;
    delete router.query.dateTo;

    router.push({
      query: {
        ...router.query,
      },
    });
  };

  useEffect(() => {
    if (isGenericTicketsError) {
      showError(genericTicketsError);
    }
  }, [isGenericTicketsError, genericTicketsError]);

  const getExportPayload = () => {
    return {
      boardId: BOARDS_ID.GENERIC_TICKETS,
      payload: {
        type: 'csv',
        rule: payload,
      },
      params: getCleanedUrlParams({
        query: searchQuery,
      }),
    };
  };

  return {
    dateFrom,
    dateTo,
    loading: isGenericTicketsLoading || teamsLoading,
    total: genericTicketsData?.meta?.total,
    data: tickets,
    page: parseInt(page, 10) || 1,
    pageSize: parseInt(pageSize, 10) || GENERIC_TICKETS_PAGE_SIZE,
    filters,
    enums: enumsResponse?.data?.data || {},
    refetchGenericTickets,
    handleChangeDateRange,
    handleFiltersChange,
    handleClearDateRange,
    getExportPayload,
  };
};
